import React, { Fragment } from 'react'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import { Icon } from '@iconify/react'
import { GoogleLogin, CredentialResponse } from '@react-oauth/google'
import { yup_password_validation } from '@pepper/utils/src/validation/validation'

import { Button } from '../../atoms'
import { TextField, PasswordField } from '../../molecules'
import styles from './_.module.css'

// regex for password https://stackoverflow.com/questions/19605150/regex-for-password-must-contain-at-least-eight-characters-at-least-one-number-a
const validationSchema = {
  details: Yup.object({
    first_name: Yup.string()
      .trim()
      .matches(
        /^[\d 'A-Za-z]+$/,
        'Your First and Last Name cannot include symbols'
      )
      .required('Your First Name is required'),
    last_name: Yup.string()
      .trim()
      .matches(
        /^[\d 'A-Za-z]+$/,
        'Your First and Last Name cannot include symbols'
      )
      .nullable(),
    email: Yup.string()
      .email('Please use a valid email to confirm your account')
      .required('Please use a valid email to confirm your account'),
    password: yup_password_validation.required('Choose a password'),
  }),
  otp: Yup.object({
    otp: Yup.string().required('Please enter the OTP sent to your email'),
  }),
}

export interface InitialFormValue {
  firstName: string
  lastName: string
  email: string
  role: string
}

interface Props {
  link: React.ElementType
  phase: 'details' | 'otp'
  onGoogleLogin: (token: string, userName?: string) => void
  onSubmit: (values: FormType, action: FormikHelpers<FormType>) => void
  onResendOtp: (values: FormType) => void
  onLoginFailure?: () => void
  signUpTitle?: string
  companyDetails?: { company_name: string | null; email: string | null }
  showGoogleLogin?: boolean
  inviteCode?: string | null
  inviteId?: string | null
  inviteHash?: string | null
  showHeading?: boolean
  showNavigation?: boolean
  initialFormValues?: Partial<InitialFormValue>
  showLoginLink?: boolean
  isClient?: boolean
  isGoogleSignupLoading?: boolean
}

export type FormType = {
  first_name: string
  last_name: string
  email: string
  password: string
  company_name: string
  otp: string | null
  invite_code?: string | null
  isUserIndividual?: boolean[]
}

export const SignUp: React.FC<Props> = ({
  link: Link,
  phase,
  showGoogleLogin = true,
  onGoogleLogin,
  onLoginFailure,
  onSubmit,
  onResendOtp,
  signUpTitle,
  companyDetails,
  inviteCode,
  inviteId = null,
  inviteHash = null,
  initialFormValues,
  showHeading = true,
  showNavigation = true,
  showLoginLink = false,
  isGoogleSignupLoading = false,
}) => {
  const getInitialEmail = (): string => {
    if (inviteCode) {
      return companyDetails?.email ?? ''
    }
    if (initialFormValues?.email) {
      return initialFormValues.email
    }
    return ''
  }

  const initialFormValue = {
    details: {
      first_name: initialFormValues?.firstName ?? '',
      last_name: initialFormValues?.lastName ?? '',
      email: getInitialEmail(),
      password: '',
    },
    otp: {
      otp: '',
    },
  }

  return (
    <main className={styles.container}>
      {showHeading ? <h1 className={styles.heading}>{signUpTitle}</h1> : null}
      <Formik
        initialValues={initialFormValue[phase]}
        onSubmit={(values: any, actions): void =>
          onSubmit(
            {
              ...values,
              first_name: values.first_name.trim(),
              last_name: values.last_name.trim(),
            },
            actions
          )
        }
        validationSchema={validationSchema[phase]}
        enableReinitialize={
          inviteCode || (initialFormValues?.email && phase === 'details')
            ? true
            : false
        }
      >
        {({
          isValid,
          isSubmitting,
          handleBlur,
          touched,
          errors,
          values,
        }): React.ReactNode => {
          if (phase === 'details')
            return (
              <Form className={styles.form}>
                {showGoogleLogin && (
                  <Fragment>
                    <div className={styles.googleLoginContainer}>
                      <GoogleLogin
                        onSuccess={(credentialResponse: CredentialResponse) => {
                          credentialResponse.credential &&
                            onGoogleLogin(credentialResponse.credential)
                        }}
                        onError={onLoginFailure}
                      />
                    </div>
                    <div className={styles.choice}>OR</div>
                  </Fragment>
                )}
                <div className={styles.formFields}>
                  <div
                    className={`${styles.formField} ${styles.formFields__name}`}
                  >
                    <TextField
                      name="first_name"
                      showError={false}
                      onBlur={handleBlur}
                      error={
                        touched.first_name && errors.first_name ? true : false
                      }
                      fieldProps={{
                        placeholder: 'First Name',
                        size: 'large',
                      }}
                    />
                    <TextField
                      name="last_name"
                      showError={false}
                      onBlur={handleBlur}
                      error={
                        touched.last_name && errors.last_name ? true : false
                      }
                      fieldProps={{
                        placeholder: 'Last Name',
                        size: 'large',
                      }}
                    />
                    {(errors.first_name || errors.last_name) &&
                    (touched.first_name || touched.last_name) ? (
                      <div className={`${styles.error} ${styles.fullWidth}`}>
                        <Icon
                          icon="ant-design:close-circle-filled"
                          className={styles.errorIcon}
                        />{' '}
                        {/* show last_name error if first_name doesn't have any errors */}
                        {errors.first_name}{' '}
                        {errors.first_name === undefined && errors.last_name}
                      </div>
                    ) : null}
                  </div>
                  <TextField
                    name="email"
                    className={`${styles.fullWidth} ${styles.formField}`}
                    onBlur={handleBlur}
                    error={touched.email && errors.email ? true : false}
                    fieldProps={{
                      placeholder: 'Email Address',
                      size: 'large',
                      disabled:
                        inviteCode || (inviteId && inviteHash) ? true : false,
                      autoComplete: 'off',
                    }}
                  />

                  <PasswordField
                    name="password"
                    fieldProps={{
                      placeholder: 'Password',
                      size: 'large',
                    }}
                    className={`${styles.fullWidth} ${styles.formField}`}
                    onBlur={handleBlur}
                    error={touched.password && errors.password ? true : false}
                  />
                  <p className={styles.note}>
                    By signing up you agree to our&nbsp;
                    <a
                      href="https://www.peppercontent.io/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </a>
                    &nbsp;and&nbsp;
                    <a
                      href="https://www.peppercontent.io/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                  <Button
                    id="gtm-signup"
                    type="primary"
                    htmlType="submit"
                    className={styles.fullWidth}
                    size="large"
                    disabled={!isValid}
                    loading={isSubmitting}
                    block={true}
                  >
                    Create Account
                  </Button>
                </div>
                {showLoginLink && (
                  <div className={styles.loginLink}>
                    <Link to={`/login${window.location?.search}`}>
                      Already have an account? Log in here
                    </Link>
                  </div>
                )}
              </Form>
            )
          else if (phase === 'otp') {
            return (
              <Form className={styles.form}>
                <h4 className="text-center">
                  Please check your email for an OTP from Pepper Content. Please
                  enter it below to confirm your email
                </h4>
                <TextField
                  name="otp"
                  className={`${styles.fullWidth} ${styles.formField}`}
                  onBlur={handleBlur}
                  error={touched.otp && errors.otp ? true : false}
                  fieldProps={{
                    placeholder: 'Enter OTP',
                    size: 'large',
                  }}
                />
                <p className={styles.resendOtpText}>
                  Did not receive the OTP?{' '}
                  <Button
                    type="link"
                    size="small"
                    onClick={() => onResendOtp(values)}
                  >
                    <span className="underline">Send Again</span>
                  </Button>
                </p>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={styles.fullWidth}
                  size="large"
                  disabled={!isValid}
                  loading={isSubmitting}
                  block={true}
                >
                  Verify OTP
                </Button>
              </Form>
            )
          }
        }}
      </Formik>
    </main>
  )
}
